import React from 'react';
import { Row, Col, Card, Badge, Alert, Table } from 'react-bootstrap';
import { 
  FileText, 
  CreditCard, 
  Send, 
  HelpCircle, 
  AlertTriangle, 
  CheckCircle2, 
  MapPin, 
  Mail, 
  ShieldAlert, 
  ClipboardCheck, 
  Sparkles,
  Printer,
  FileCheck
} from 'lucide-react';

export default function GenInstruction() {
  return (
    <div className="py-2 px-1 px-md-3" style={{ background: '#f8fafc' }}>
      <div className="mx-auto" style={{ maxWidth: '1000px' }}>
        
        {/* Header Title & Intro */}
        <div className="text-center mb-4">
          <Badge bg="primary" className="px-3 py-2 rounded-pill mb-2 fw-semibold">
            <Sparkles size={14} className="me-1" /> Recruitment Guidelines 2026-27
          </Badge>
          <h2 className="fw-bold text-dark mb-1">General Instructions for Applicants</h2>
          <p className="text-muted small mx-auto" style={{ maxWidth: '650px' }}>
            Please read these guidelines thoroughly before filling out the online application. Complete both the online submission and postal verification steps.
          </p>
        </div>

        {/* Highlight Stats / Quick Overview */}
        <Row className="g-3 mb-4">
          <Col md={4} sm={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100 bg-white p-3 text-center border-top border-4 border-primary">
              <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-circle d-inline-block mb-2">
                <CreditCard size={24} />
              </div>
              <h6 className="fw-bold text-dark mb-1">Application Fee</h6>
              <div className="fs-5 fw-bold text-primary">₹ 200 / Post</div>
              <small className="text-muted">Payable via PhonePe / UPI / NetBanking</small>
            </Card>
          </Col>

          <Col md={4} sm={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100 bg-white p-3 text-center border-top border-4 border-success">
              <div className="bg-success bg-opacity-10 text-success p-2 rounded-circle d-inline-block mb-2">
                <FileCheck size={24} />
              </div>
              <h6 className="fw-bold text-dark mb-1">Two-Step Process</h6>
              <div className="fs-6 fw-bold text-success">Online + Physical Copy</div>
              <small className="text-muted">Online form submission + Postal dispatch</small>
            </Card>
          </Col>

          <Col md={4} sm={12}>
            <Card className="border-0 shadow-sm rounded-4 h-100 bg-white p-3 text-center border-top border-4 border-info">
              <div className="bg-info bg-opacity-10 text-info p-2 rounded-circle d-inline-block mb-2">
                <Mail size={24} />
              </div>
              <h6 className="fw-bold text-dark mb-1">Applicant Helpline</h6>
              <div className="fs-6 fw-bold text-dark">support@idntica.com</div>
              <small className="text-muted">Technical & application query support</small>
            </Card>
          </Col>
        </Row>

        {/* 4-Step Process Walkthrough */}
        <Card className="border-0 shadow-soft rounded-4 bg-white p-3 p-md-4 mb-4">
          <Card.Body className="p-0">
            <h5 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
              <ClipboardCheck size={22} className="text-primary" />
              <span>Step-by-Step Application Workflow</span>
            </h5>

            <Row className="g-3">
              <Col md={6}>
                <div className="d-flex gap-3 p-3 rounded-3 bg-light border h-100">
                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    1
                  </div>
                  <div>
                    <h6 className="fw-bold text-dark mb-1">Fill Online Form</h6>
                    <p className="text-muted small mb-0">
                      Provide accurate personal, family, and educational details. Ensure applicant name strictly matches your 10th (SSLC) certificate.
                    </p>
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="d-flex gap-3 p-3 rounded-3 bg-light border h-100">
                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    2
                  </div>
                  <div>
                    <h6 className="fw-bold text-dark mb-1">Settle Application Fee</h6>
                    <p className="text-muted small mb-0">
                      Pay ₹200 securely using PhonePe (UPI, Debit Card, NetBanking). Fee is non-refundable. Demand Drafts are <strong>not accepted</strong>.
                    </p>
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="d-flex gap-3 p-3 rounded-3 bg-light border h-100">
                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    3
                  </div>
                  <div>
                    <h6 className="fw-bold text-dark mb-1">Print Application & Affix Photo</h6>
                    <p className="text-muted small mb-0">
                      Download the filled application from the <strong>Print Application</strong> tab. Affix your recent passport-size photograph with your signature.
                    </p>
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="d-flex gap-3 p-3 rounded-3 bg-light border h-100">
                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    4
                  </div>
                  <div>
                    <h6 className="fw-bold text-dark mb-1">Send by Post / Courier</h6>
                    <p className="text-muted small mb-0">
                      Enclose self-attested photocopies of certificates in the specified order and dispatch to the Co-ordinator's office before the deadline.
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Required Documents Checklist */}
        <Card className="border-0 shadow-soft rounded-4 bg-white p-3 p-md-4 mb-4">
          <Card.Body className="p-0">
            <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <FileText size={22} className="text-primary" />
              <span>Mandatory Document Enclosure Order</span>
            </h5>
            <p className="text-muted small mb-4">
              Attach self-attested photocopies of certificates with the printed application form in the exact order below:
            </p>

            <Row className="g-4">
              {/* B.T. Assistant Document List */}
              <Col md={6}>
                <Card className="border rounded-4 p-3 bg-light h-100">
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <span className="fw-bold text-primary">For B.T. Assistant Post</span>
                    <Badge bg="primary" className="rounded-pill">6 Documents</Badge>
                  </div>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 small">
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>1.</strong> 10th / SSLC Mark Sheet</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>2.</strong> 12th / Higher Secondary (HSC) Mark Sheet</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>3.</strong> UG Consolidated Mark Sheet</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>4.</strong> UG Degree Certificate</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>5.</strong> D.T.Ed / B.Ed Degree Certificate</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>6.</strong> TET Qualified Certificate</span>
                    </li>
                  </ul>
                </Card>
              </Col>

              {/* P.G. Assistant Document List */}
              <Col md={6}>
                <Card className="border rounded-4 p-3 bg-light h-100">
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <span className="fw-bold text-primary">For P.G. Assistant Post</span>
                    <Badge bg="primary" className="rounded-pill">6 Documents</Badge>
                  </div>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 small">
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>1.</strong> 10th / SSLC Mark Sheet</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>2.</strong> 12th / Higher Secondary (HSC) Mark Sheet</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>3.</strong> UG Consolidated Mark Sheet</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>4.</strong> UG Degree Certificate</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>5.</strong> PG Consolidated Mark Sheet</span>
                    </li>
                    <li className="d-flex align-items-center gap-2 text-dark">
                      <CheckCircle2 size={16} className="text-success flex-shrink-0" />
                      <span><strong>6.</strong> PG Degree Certificate</span>
                    </li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Important Terms & Dispatch Address Section */}
        <Row className="g-4 mb-3">
          <Col md={7}>
            <Card className="border-0 shadow-soft rounded-4 bg-white p-3 p-md-4 h-100">
              <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <ShieldAlert size={22} className="text-primary" />
                  <span>Important Rules & Guidelines</span>
                </h5>
                <Badge bg="light" text="dark" className="border fw-normal px-2.5 py-1">
                  Key Policies
                </Badge>
              </div>

              <Row className="g-3">
                {/* 1. Corrections */}
                <Col sm={6}>
                  <div className="p-3 rounded-3 bg-light border h-100 d-flex flex-column">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div className="bg-primary bg-opacity-10 text-primary p-1.5 rounded-2 d-flex align-items-center justify-content-center flex-shrink-0">
                        <FileText size={16} />
                      </div>
                      <span className="fw-bold text-dark small">Data Corrections</span>
                    </div>
                    <p className="text-muted small mb-0 lh-sm" style={{ fontSize: '0.82rem' }}>
                      Verify all information carefully. Any corrections must be made <strong>before online payment</strong>.
                    </p>
                  </div>
                </Col>

                {/* 2. Rejections */}
                <Col sm={6}>
                  <div className="p-3 rounded-3 bg-light border h-100 d-flex flex-column">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div className="bg-danger bg-opacity-10 text-danger p-1.5 rounded-2 d-flex align-items-center justify-content-center flex-shrink-0">
                        <AlertTriangle size={16} />
                      </div>
                      <span className="fw-bold text-dark small">Form Rejection</span>
                    </div>
                    <p className="text-muted small mb-0 lh-sm" style={{ fontSize: '0.82rem' }}>
                      Incomplete forms or missing certificates will lead to <strong>automatic rejection</strong> without notice.
                    </p>
                  </div>
                </Col>

                {/* 3. Fee Policy */}
                <Col sm={6}>
                  <div className="p-3 rounded-3 bg-light border h-100 d-flex flex-column">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div className="bg-warning bg-opacity-10 text-dark p-1.5 rounded-2 d-flex align-items-center justify-content-center flex-shrink-0">
                        <CreditCard size={16} />
                      </div>
                      <span className="fw-bold text-dark small">Fee & DD Policy</span>
                    </div>
                    <p className="text-muted small mb-0 lh-sm" style={{ fontSize: '0.82rem' }}>
                      Application fee of ₹200 is <strong>non-refundable</strong>. Demand Drafts (DD) are <strong>not accepted</strong>.
                    </p>
                  </div>
                </Col>

                {/* 4. Multiple Applications */}
                <Col sm={6}>
                  <div className="p-3 rounded-3 bg-light border h-100 d-flex flex-column">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div className="bg-success bg-opacity-10 text-success p-1.5 rounded-2 d-flex align-items-center justify-content-center flex-shrink-0">
                        <CheckCircle2 size={16} />
                      </div>
                      <span className="fw-bold text-dark small">Multiple Positions</span>
                    </div>
                    <p className="text-muted small mb-0 lh-sm" style={{ fontSize: '0.82rem' }}>
                      Applying for multiple posts requires <strong>separate online forms, fees, and postal packets</strong>.
                    </p>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Postal Address Card */}
          <Col md={5}>
            <Card className="border-0 shadow-soft rounded-4 text-white p-3 p-md-4 h-100" style={{ background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' }}>
              <h5 className="fw-bold text-white mb-3 d-flex align-items-center gap-2">
                <MapPin size={22} />
                <span>Postal Dispatch Address</span>
              </h5>
              <p className="small text-white-50 mb-3">
                Send printed application with photocopies by registered post / speed post / courier to:
              </p>
              <div className="bg-white bg-opacity-10 rounded-3 p-3 border border-white border-opacity-20 mb-3">
                <div className="fw-bold text-white fs-6">The Co-ordinator</div>
                <div className="text-white small">Madurai Province Jesuit Schools Educational Commission</div>
                <div className="text-white small">St. Mary's Higher Secondary School Campus</div>
                <div className="text-white-50 small mt-1">Madurai - 625001, Tamil Nadu, India</div>
              </div>
              <div className="small text-white-50">
                <span className="fw-bold text-white">Note:</span> Also submit a duplicate copy to the Correspondent of the specific school applied for.
              </div>
            </Card>
          </Col>
        </Row>

      </div>
    </div>
  );
}
